import React, { useState } from "react";
import {
  formatDateTime,
  Table,
  TableConfig, // eslint-disable-line no-unused-vars
  TableData, // eslint-disable-line no-unused-vars
  ModalPopup,
  BasicTable, // eslint-disable-line no-unused-vars
} from "isuppli-react-components";

import { useDispatch } from "react-redux";
import {
  DetailedAuditTrailMessage, // eslint-disable-line no-unused-vars
  getDetailedAuditRecords,
  AuditDetailMessage, // eslint-disable-line no-unused-vars
} from "../../../http/detailedaudit/detailedaudit";
import { showError } from "../../../store/actions";
import { auditActionTypeToString } from "../../../util/Enumerators/AuditActionType";
import { iSuppliServiceTypeToString } from "../../../util/Enumerators/ISuppliServiceType";
import Styles from "./DetailedAuditTable.module.scss";

interface AuditRecords<T> {
  [Key: string]: T;
}

const DetailedAuditTable = (
  {
    data,
    loading,
    onPageChange,
  }
    :
    {
      data: TableData<DetailedAuditTrailMessage>,
      loading: boolean,
      onPageChange: (limit: number, offset: number) => void;
    }
) => {
  const [records, setrecords] = useState<AuditRecords<Array<AuditDetailMessage>>>({});
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const dispatch = useDispatch();

  const tableConfig: TableConfig<number, DetailedAuditTrailMessage> = {
    columns: [
      {
        key: "date",
        heading: "Date & Time",
        valueTemplate: (row) => <span>{formatDateTime(row.dateTime)}</span>,
        size: "1fr",
      },
      {
        key: "registeredCompanyName",
        heading: "Registered Company Name",
        propName: "registeredCompanyName",
        size: "1fr",
      },
      {
        key: "tablesAffected",
        heading: "Tables Affected",
        valueTemplate: (row) => <span>{row.affectedTables.join(", ")}</span>,
        size: "1fr",
      },
      {
        key: "user",
        heading: "Performed by",
        valueTemplate: (row) => (
          <span>
            {`${
              row.auditUser?.fullName != null
                ? row.auditUser.fullName
                : "System Action"}
              ${row.auditUser?.email != null
              ? `(${row.auditUser.email})`
              : ""
          }`}
          </span>
        ),
        size: "1fr",
      },
      {
        key: "serviceType",
        heading: "Service Type",
        valueTemplate: (row) => <span>{iSuppliServiceTypeToString(row.serviceType)}</span>,
        size: "1fr",
      },
    ],
  };

  const viewMoreHandler = async (detailedAuditId: number) => {
    setLoadingRecords(true);
    try {
      // API call
      const result = await getDetailedAuditRecords(detailedAuditId);

      const dictionary: AuditRecords<Array<AuditDetailMessage>> = result.reduce((r, a) => {
        const reduced = { ...r };
        if (reduced[a.tableName] == null) { reduced[a.tableName] = []; }
        reduced[a.tableName].push(a);
        return reduced;
      }, Object.create(null));
      setrecords(dictionary);

      // show modal
      setShowRecordsModal(true);
    } catch (error) {
      dispatch(showError());
    }
    setLoadingRecords(false);
  };

  const buildTables = () => {
    const tables: JSX.Element[] = [];

    const keys = Object.keys(records);
    keys.forEach((key: string) => {
      const tableItems = records[key];
      const tableName = key;

      const formattedTableItems = tableItems.map((item) => {
        // key values
        let formattedKeyValues: JSX.Element[] = [];
        const keyValuesObj = JSON.parse(item?.keyValues);
        if (keyValuesObj != null) {
          const entries = Object.entries(keyValuesObj);
          formattedKeyValues = entries.map((entry) => (
            <li key={entry[0]}>
              <p>
                <b>
                  {entry[0]}
                  :
                </b>
                {" "}
                {entry[1]}
              </p>
            </li>
          ));
        }

        // old values
        let formattedOldValues: JSX.Element[] = [];
        const oldValuesObj = JSON.parse(item?.oldValues);
        if (oldValuesObj != null) {
          const entries = Object.entries(oldValuesObj);
          formattedOldValues = entries.map((entry) => (
            <li key={entry[0]}>
              <p>
                <b>
                  {entry[0]}
                  :
                </b>
                {" "}
                {entry[1]}
              </p>
            </li>
          ));
        }

        // new values
        let formattedNewValues: JSX.Element[] = [];
        const newValuesObj = JSON.parse(item?.newValues);
        if (newValuesObj != null) {
          const entries = Object.entries(newValuesObj);
          formattedNewValues = entries.map((entry) => (
            <li key={entry[0]}>
              <p>
                <b>
                  {entry[0]}
                  :
                </b>
                {" "}
                {entry[1]}
              </p>
            </li>
          ));
        }

        return {
          ...item,
          formattedKeyValues,
          formattedOldValues,
          formattedNewValues,
        };
      });

      const table = (
        <div key={tableName} className="px-3">
          <div>
            <h4 className="mt-2">{tableName}</h4>
          </div>
          <BasicTable
            emptyMessage="No detailed audit records to view"
            loading={loadingRecords}
            columns={[{
              key: "dateChanged",
              text: "Date Changed",
              align: "left",
              size: "1fr",
            },
            {
              key: "actionType",
              text: "Action",
              align: "left",
              size: "1fr",
            },
            {
              key: "keyValues",
              text: "Key Values",
              align: "left",
              size: "1fr",
            },
            {
              key: "oldValues",
              text: "Old Values",
              align: "left",
              size: "1fr",
            },
            {
              key: "newValues",
              text: "New Values",
              align: "left",
              size: "1fr",
            },
            ]}
            rows={formattedTableItems.map((tableItem) => [
              {
                key: "dateChanged",
                value: <span>{formatDateTime(tableItem.dateChanged)}</span>,
              },
              {
                key: "actionType",
                value: auditActionTypeToString(tableItem.actionType),
              },
              {
                key: "keyValues",
                value: <ul className={Styles.AuditRecordList}>{tableItem.formattedKeyValues}</ul>,
              },
              {
                key: "oldValues",
                value: <ul className={Styles.AuditRecordList}>{tableItem.formattedOldValues}</ul>,
              },
              {
                key: "newValues",
                value: <ul className={Styles.AuditRecordList}>{tableItem.formattedNewValues}</ul>,
              },
            ])}
          />
        </div>
      );
      tables.push(table);
    });

    if (tables.length > 0) {
      return tables;
    }

    return (
      <div className="px-3">
        No detailed audit records to view
      </div>
    );
  };

  return (
    <>
      {
        showRecordsModal
        && (
          <ModalPopup
            onClose={() => setShowRecordsModal(false)}
            heading="Detailed Audit Records"
            subheading=""
          >
            <div>
              {buildTables()}
            </div>
          </ModalPopup>
        )
      }
      <Table
        onPageChange={onPageChange}
        tableConfig={tableConfig}
        loading={loading}
        tableStyle="default"
        data={data}
        onViewMore={(row) => viewMoreHandler(row.id)}
      />
    </>
  );
};

export default DetailedAuditTable;
